import { Component, Inject, Input, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FiltersService } from 'src/app/core/services/filters.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { MetricsService } from 'src/app/core/services/metrics.service';
import { ServiceInsightsService } from '../../services/service-insights-service.service';
import { PageEvent } from '@angular/material/paginator';
import {
  ExpandedPhotosResult,
  GroupedPhotos,
  PhotoInfo,
  SICallTypes,
  SICallTypesDatabaseCode,
} from '../../types/service-insight-types';
import { ContractModel } from 'src/app/global/contract-filter-model';
import {
  ServiceCustomerFilterOptions,
  ServiceCustomerFilters,
} from 'src/app/global/service-customer-model';
import { PhotoSelectionStore } from 'src/app/shared/stores/photo-selection-store';
import {
  DOWNLOAD_SETTINGS,
  DownloadSettings,
} from 'src/app/config/AppInitializer.factory';

/**
 * Displays grouped photo data for Service Insights.
 *
 * This component supports two photo-based experiences:
 * - Photo Observations @see SICallTypes.PhotoObservations, where photos are the
 *   primary dataset and grouping is controlled by the parent component.
 * - Service Metrics photo views, where photos are displayed as supporting data
 *   for a specific service call type (Regular Service Calls, Program Reviews,
 *   Extra Service Visits, etc.).
 */
@Component({
    selector: 'photos-table',
    templateUrl: './photos-table.component.html',
    styleUrls: ['./photos-table.component.scss'],
    animations: [
        trigger('inOutAnimation', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('.4s ease-out', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                style({ opacity: 1 }),
                animate('.4s ease-in', style({ opacity: 0 })),
            ]),
        ]),
    ],
    standalone: false
})
export class PhotosTable implements OnInit {
  // photos-view
  photosList: any[] = [];
  groupedKey: string = '';
  currentIndex: number = 0;
  isPhotoViewOpened: boolean = false;

  groupByOptions: string[] = ['By Date', 'By Location', 'By Issues'];
  selectedExpandedPhotos: ExpandedPhotosResult = {
    count: 0,
    totalCount: 0,
    photos: [],
  };

  expandedPhotos: { [id: string]: { [id: number]: ExpandedPhotosResult } } = {
    endDate: { 0: { count: 0, totalCount: 0, photos: [] } },
    location: { 0: { count: 0, totalCount: 0, photos: [] } },
    issue: { 0: { count: 0, totalCount: 0, photos: [] } },
  };

  groupedResult: GroupedPhotos[] = [];
  hideRuleContent: boolean[] = [];

  isDataLoading: boolean = true;
  isToolbarLoading: boolean = true;
  private isInitializing: boolean = false;
  noDataAvailable = false;

  @Input() title: string = 'Title';
  @Input() callType: SICallTypes = SICallTypes.UntrackedCalls;
  @Input() groupByValue: string = '';

  advancedFilterData: ServiceCustomerFilters[] = [];
  excludeKeys: string[] = [];
  toggleAdvancedFilterSelection: boolean = false;

  private readonly callTypesWithRecurring: SICallTypes[] = [
    SICallTypes.ExtraServiceVisit,
    SICallTypes.RegularServiceCalls,
    SICallTypes.ProgramReviews,
  ];

  //expose enum for html
  SICallTypes = SICallTypes;

  totalRows = 0;
  pageSize = 100;
  currentPage = 0;
  pageSizeOptions: number[] = [25, 50, 75, 100];

  filters: ContractModel | undefined;
  expandedPhotosSubscription: Subscription | undefined;

  selectedAdvancedFilters: ServiceCustomerFilterOptions = {
    accountNumber: [],
    accountName: [],
    country: [],
    state: [],
    city: [],
    zipCode: [],
    recurringIssue: [],
  };

  serviceCustomerFiltersSubscription: Subscription | undefined;

  //Link to work order in salesforce
  viewReportURL: string =
    'https://ecolab.lightning.force.com/lightning/r/WorkOrder/';

  // track collapsed status of all rows
  areAllRowsCollapsed: boolean = false;

  // Track the number of selected photos per group. Better performance than filtering and fetching the count from the store every time.
  selectedCountsPerGroup: Record<string, number> = {};

  constructor(
    private _filterService: FiltersService,
    private _metricsService: MetricsService,
    private _siService: ServiceInsightsService,
    private _photoSelectionStore: PhotoSelectionStore,
    @Inject(DOWNLOAD_SETTINGS) private _downloadSettings: DownloadSettings,
  ) {}


  async ngOnInit() {
    this.selectedAdvancedFilters = {
      accountNumber: [],
      accountName: [],
      country: [],
      state: [],
      city: [],
      zipCode: [],
      recurringIssue: [],
    };

    this._filterService.selectedFiltersObservable.subscribe(
      (f) => (this.filters = f),
    );

    this.expandedPhotosSubscription = this._siService
      .getExpandedPhotosByGroup('endDate')
      .subscribe((data) => (this.expandedPhotos['endDate'] = data));
    this.expandedPhotosSubscription = this._siService
      .getExpandedPhotosByGroup('location')
      .subscribe((data) => (this.expandedPhotos['location'] = data));
    this.expandedPhotosSubscription = this._siService
      .getExpandedPhotosByGroup('issue')
      .subscribe((data) => (this.expandedPhotos['issue'] = data));

    if (!this.callTypesWithRecurring.includes(this.callType)) {
      this.excludeKeys = ['recurringIssue'];
    }

    // Single await Promise.all() is used to load photos data and advanced filters in parallel,
    // improving performance and better user experience.
    // isInitializing keeps isDataLoading=true for the entire init sequence to prevent
    // intermediate flickers from loadPhotosData() resolving before advancedFilterData is set,
    // and to suppress the BehaviorSubject's synchronous initial emission.
    // Individual loading states for toolbar and table are used to display respective skeleton loaders.
    this.isInitializing = true;
    const [, advancedFilters] = await Promise.all([
      this.loadPhotosData(),
      this._siService.getServiceInsightsAdvFilters(this.callType),
    ]);
    this.advancedFilterData = advancedFilters;
    this.isToolbarLoading = false;

    this.serviceCustomerFiltersSubscription = this._siService
      .getServiceCustomerFilters()
      .subscribe(async (advFilter) => {
        this.selectedAdvancedFilters = advFilter;
        if (!this.isInitializing) {
          await this.loadPhotosData(true);
        }
      });

    this.isInitializing = false;
    this.isDataLoading = false;
  }

  /**
   * Triggered when the Input is updated. For this component,
   * only the groupByValue input is being updated during the component lifecycle.
   * More specifically, groupByValue input gets updated from service-details-calls
   * when SICallType is PhotoObservations.
   */
  // TODO: US620782 - Replace magic strings for groupByValue with constants or enums.
  // Address the variation within accountNumber and issues. If these values are sent to the API,
  // they should be normalized to match the expected groupByValue keys to reduce potential mismatches and errors.
  async ngOnChanges() {
    if (this.callType === SICallTypes.PhotoObservations) {
      //service-details-call component sends groupByValue as 'endDate', 'accountNumber', 'issues'
      if (this.groupByValue == 'endDate') this.groupByValue = 'endDate';
      else if (
        this.groupByValue == 'accountNumber' ||
        this.groupByValue == 'location'
      )
        this.groupByValue = 'location';
      else if (this.groupByValue == 'issues' || this.groupByValue == 'issue')
        this.groupByValue = 'issue';

      this.currentPage = 0;
      await this.loadPhotosData(); //.then(() => {})
    }
  }

  public isRequiredGroupOption(option: string): boolean {
    if (option == 'By Issues') {
      if (
        this.callType !== SICallTypes.ProgramReviews &&
        this.callType !== SICallTypes.RegularServiceCalls
      )
        return false;
    }
    return true;
  }

  private async loadPhotosData(refresh = false) {
    if (!this.isInitializing) this.isDataLoading = true;

    if (this.expandedPhotos[this.groupByValue].hasOwnProperty(this.currentPage))
      this.selectedExpandedPhotos =
        this.expandedPhotos[this.groupByValue][this.currentPage];
    else this.selectedExpandedPhotos = { count: 0, totalCount: 0, photos: [] };

    if (
      (this.selectedExpandedPhotos &&
        this.selectedExpandedPhotos.photos.length == 0 &&
        this.selectedExpandedPhotos.count == 0) ||
      refresh
    ) {
      let photos = await this.getSIExpandedPhotosFromAPI(
        this.callType,
        this.selectedAdvancedFilters,
      );

      this._siService.setExpandedPhotosByGroup(
        this.groupByValue as keyof typeof this._siService.expandedPhotosByGroup,
        this.currentPage,
        photos,
      );

      this.selectedExpandedPhotos =
        this.expandedPhotos[this.groupByValue][this.currentPage];
    }

    this.groupedResult = this.selectedExpandedPhotos.photos;
    this.initializeGroupCounts();
    this.totalRows = this.selectedExpandedPhotos.totalCount;

    if (!this.isInitializing) this.isDataLoading = false;
  }

  async getSIExpandedPhotosFromAPI(
    callType: SICallTypes,
    serviceCustomerFilters: ServiceCustomerFilterOptions,
  ): Promise<ExpandedPhotosResult> {
    let payload: {
      callType: SICallTypes;
      groupType: string;
      serviceCustomerFilters: ServiceCustomerFilterOptions;
      limit: number;
      page: number;
    } = {
      callType: callType,
      groupType: this.groupByValue,
      serviceCustomerFilters: serviceCustomerFilters,
      limit: this.pageSize,
      page: this.currentPage + 1,
    };

    if (this.filters != undefined)
      return await this._siService.getSIExpandedPhotosFromAPI(payload);
    else return { count: 0, totalCount: 0, photos: [] };
  }

  displayGroupValue() {
    if (this.groupByValue === 'endDate') return this.groupByOptions[0];
    else if (this.groupByValue === 'location') return this.groupByOptions[1];
    else if (this.groupByValue === 'issue') return this.groupByOptions[2];
    else return '';
  }

  onGroupByChange(value: string) {
    if (value == this.groupByOptions[0]) this.groupByValue = 'endDate';
    else if (value == this.groupByOptions[1]) this.groupByValue = 'location';
    else if (value == this.groupByOptions[2]) this.groupByValue = 'issue';
    else this.groupByValue = '';

    this.currentPage = 0;

    this.loadPhotosData().then(() => {});
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;

    if (this.pageSize != event.pageSize) {
      this._siService.clearAllExpandedPhotos();
    }

    this.pageSize = event.pageSize;
    this.loadPhotosData().then(() => {});
  }

  openPhotoInPhotoView(groupIndex: number, itemIndex: number) {
    this.photosList = [];

    this.groupedKey = this.groupedResult[groupIndex].sortBy;
    this.photosList = this.groupedResult[groupIndex].photos;
    this.currentIndex = itemIndex;

    this.isPhotoViewOpened = true;
  }

  closePhotoView() {
    this.isPhotoViewOpened = false;
  }

  toggle(index: number) {
    // toggle based on index.
    // Toggles the bool value of a group (index) that sets a group in expanded or collapsed state.
    this.hideRuleContent[index] = !this.hideRuleContent[index];

    this.areAllRowsCollapsed =
      this.groupedResult.length > 0 &&
      this.groupedResult.every((_, i) => this.hideRuleContent[i] === true);
  }

  toggleAll(b: boolean) {
    let i = 0;
    for (let group of this.groupedResult) {
      this.hideRuleContent[i++] = b;
    }

    this.areAllRowsCollapsed = b;
  }

  resetToggle() {
    this.hideRuleContent = [];
  }

  togglePhotoSelection(photo: PhotoInfo, group: GroupedPhotos, event: Event) {
    const maxPhotoSelection = this._downloadSettings.maxPhotoSelection;
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      if (this._photoSelectionStore.count() >= maxPhotoSelection) {
        checkbox.checked = false;
        alert(
          `You can only select up to ${maxPhotoSelection} photos for download. Please deselect some photos before adding new ones.`,
        );
        return;
      }
    }

    this._photoSelectionStore.toggle(photo);

    // Update the count of selected photos for the group
    if(checkbox.checked) {
      this.selectedCountsPerGroup[group.sortBy]++;
    } else {
      this.selectedCountsPerGroup[group.sortBy]--;
    }
  }

  isPhotoSelected(photo: PhotoInfo): boolean {
    return this._photoSelectionStore.isSelected(photo);
  }

  openPhotoInNewTab(salesforceUrl: string) {
    window.open(salesforceUrl, '_blank');
  }

  /**
   * Initialize the selectedCountsPerGroup object with the count of selected photos for each group.
   * This is done to improve performance by avoiding filtering and counting from the store every time the count is needed.
   */
  initializeGroupCounts() {
    this.selectedCountsPerGroup = {};

    this.groupedResult.forEach((group) => {
      this.selectedCountsPerGroup[group.sortBy]  = group.photos.filter(photo =>
        this._photoSelectionStore.isSelected(photo)
      ).length;
    });
  }

  /**
   * Given the database code of a service call, return the unit mapped to the SICallType.
   * The unit is just the non-plural form of the SICallType, e.g. 'Installation' for SICallTypes.Installations.
   * @param callTypeCode
   * @returns
   *
   * @deprecated This method is being deprecated and will be removed in future versions once legacy UI is fully migrated.
   * @see PhotoCardComponent.mapServiceCallKeyToUnit() for the implementation.
   */
  public mapServiceCallKeyToUnit(callTypeCode: string): string {
    //Get SICallType given the database code
    let entry = Object.entries(SICallTypesDatabaseCode).find(
      ([key, value]) => value === callTypeCode,
    );
    let callType: SICallTypes =
      entry == undefined
        ? SICallTypes.UntrackedCalls
        : (entry[0] as SICallTypes);
    let callTypeUnit = this._metricsService.mapServiceCallKeyToUnit(callType);
    return callTypeUnit;
  }

  async onSelectedFiltersChange(selectedFilters: {
    [id: string]: string[];
  }): Promise<void> {
    this._siService.clearAllExpandedPhotos();
    const serviceCustomerFilters =
      selectedFilters as ServiceCustomerFilterOptions;
    this._siService.setServiceCustomerFilters(serviceCustomerFilters);
  }

  async ngOnDestroy() {
    this.serviceCustomerFiltersSubscription?.unsubscribe();
    this._siService.clearAllExpandedPhotos();
  }
}
